import {
  BikeType,
  RidingStyle,
  RidingExperience,
  Flexibility,
  PainPoint,
  BIKE_OPTIONS,
  RIDING_STYLE_LABELS,
} from '../constants/bikeConfig';
import { BikeFitAssessment, AngleAssessment } from './fitAssessment';
import { PedalAnalysis, PelvicRockResult } from '../modules/ProcessingPipeline';

// Maps internal status names to the spec's colour names
const STATUS_MAP: Record<string, string> = {
  good:            'green',
  borderline:      'amber',
  needs_attention: 'red',
};

// Human-readable labels for ranges_applied section
const EXPERIENCE_MODIFIER_LABELS: Record<RidingExperience, string> = {
  beginner:     'beginner (knee −2°, hip +5°, trunk +3°)',
  intermediate: 'intermediate (no change)',
  experienced:  'experienced (knee +2°, hip −3°, trunk −2°)',
};

const FLEXIBILITY_MODIFIER_LABELS: Record<Flexibility, string> = {
  good:    'good (no change)',
  average: 'average (hip_min +5°, trunk_min +3°)',
  limited: 'limited (hip_min +10°, trunk_min +7°)',
};

// Mirrors the values in conversion_table_actions.json
const CONVERSION = {
  knee_bdc: { degrees_to_mm: 2.5, max_single_change_mm: 15 },
  trunk:    { degrees_to_mm: 3.0, max_single_change_mm: 15 },
  elbow:    { degrees_to_mm: 5.0, max_single_change_mm: 20 },
} as const;

function calcMm(deviation: number, key: keyof typeof CONVERSION): number {
  const { degrees_to_mm, max_single_change_mm } = CONVERSION[key];
  return Math.min(Math.round(Math.abs(deviation) * degrees_to_mm), max_single_change_mm);
}

function rangeEdge(assessment: AngleAssessment, deviation: number): number | null {
  if (!assessment.range) return null;
  return deviation > 0 ? assessment.range.max : assessment.range.min;
}

export interface AdjustmentResult {
  needed: boolean;
  direction?: string;
  amount_mm?: number;
  reason: string;
  priority?: number;
}

export function buildSaddleHeight(assessment: AngleAssessment, measured: number): AdjustmentResult {
  if (assessment.status === 'good' || assessment.status === 'no_range') {
    return { needed: false, reason: 'Knee angle within range' };
  }
  const edge = rangeEdge(assessment, assessment.deviation);
  const side = assessment.deviation > 0 ? 'above' : 'below';
  const limitWord = assessment.deviation > 0 ? 'maximum' : 'minimum';
  return {
    needed: true,
    direction: assessment.deviation > 0 ? 'lower' : 'raise',
    amount_mm: calcMm(assessment.deviation, 'knee_bdc'),
    reason: `Knee angle ${side} range (${measured.toFixed(1)}° vs ${limitWord} ${edge}°)`,
    priority: 1,
  };
}

export function buildHandlebarHeight(assessment: AngleAssessment, measured: number): AdjustmentResult {
  if (assessment.status === 'good' || assessment.status === 'no_range') {
    return { needed: false, reason: 'Trunk angle within range' };
  }
  const edge = rangeEdge(assessment, assessment.deviation);
  const side = assessment.deviation > 0 ? 'above' : 'below';
  const limitWord = assessment.deviation > 0 ? 'maximum' : 'minimum';
  return {
    needed: true,
    direction: assessment.deviation > 0 ? 'lower' : 'raise',
    amount_mm: calcMm(assessment.deviation, 'trunk'),
    reason: `Trunk angle ${side} range (${measured.toFixed(1)}° vs ${limitWord} ${edge}°)`,
    priority: 2,
  };
}

export function buildStemLength(assessment: AngleAssessment, measured: number): AdjustmentResult {
  if (assessment.status === 'good' || assessment.status === 'no_range') {
    return { needed: false, reason: 'Elbow angle within range' };
  }
  // shorter_stem_if_above_range → above = shorten reach, below = lengthen reach
  const edge = rangeEdge(assessment, assessment.deviation);
  const side = assessment.deviation > 0 ? 'above' : 'below';
  const limitWord = assessment.deviation > 0 ? 'maximum' : 'minimum';
  return {
    needed: true,
    direction: assessment.deviation > 0 ? 'shorten' : 'lengthen',
    amount_mm: calcMm(assessment.deviation, 'elbow'),
    reason: `Elbow angle ${side} range (${measured.toFixed(1)}° vs ${limitWord} ${edge}°)`,
    priority: 3,
  };
}

export function buildSaddleForeAft(assessment: AngleAssessment, measured: number): AdjustmentResult {
  if (assessment.status === 'good' || assessment.status === 'no_range') {
    return { needed: false, reason: 'Hip angle within range' };
  }
  const edge = rangeEdge(assessment, assessment.deviation);
  const side = assessment.deviation > 0 ? 'above' : 'below';
  const limitWord = assessment.deviation > 0 ? 'maximum' : 'minimum';
  return {
    needed: true,
    direction: assessment.deviation > 0 ? 'backward' : 'forward',
    reason: `Hip angle ${side} range (${measured.toFixed(1)}° vs ${limitWord} ${edge}°) — complex adjustment, multiple causes possible`,
    priority: 4,
  };
}

function buildAngleEntry(
  measured: number,
  assessment: AngleAssessment,
  label: string,
  informational = false
): object {
  if (informational || assessment.status === 'no_range') {
    return { measured: parseFloat(measured.toFixed(1)), label };
  }
  return {
    measured: parseFloat(measured.toFixed(1)),
    range_min: assessment.range!.min,
    range_max: assessment.range!.max,
    status: STATUS_MAP[assessment.status] ?? assessment.status,
    label,
  };
}

function pelvicRockNote(result: PelvicRockResult | null): string {
  if (!result || result.mm === null) return 'Pelvic rock data not available from this video analysis.';
  if (result.status === 'green')  return `Pelvic stability looks good at ${result.mm}mm. Ideal is under 20mm.`;
  if (result.status === 'amber')  return `Moderate pelvic rocking detected at ${result.mm}mm. Ideal is under 20mm.`;
  return `Significant pelvic rocking detected at ${result.mm}mm. Ideal is under 20mm.`;
}

export interface SessionData {
  session_id: string;
  timestamp: string;
  rider_profile: object;
  measurements: object;
  calculated_adjustments: object;
  stability: object;
  ranges_applied: object;
}

export function buildSessionData(
  sessionId: string,
  bikeType: BikeType,
  ridingStyle: RidingStyle,
  experience: RidingExperience,
  flexibility: Flexibility,
  painPoints: PainPoint[],
  bdc: PedalAnalysis,
  tdc: PedalAnalysis,
  trunkAngleAvg: number | null,
  trunkAngleDivergence: boolean,
  fitAssessment: BikeFitAssessment,
  pelvicRock: PelvicRockResult | null
): SessionData {
  const bikeLabel  = BIKE_OPTIONS[bikeType]?.label ?? bikeType;
  const styleLabel = RIDING_STYLE_LABELS[ridingStyle] ?? ridingStyle;

  const activePains   = painPoints.filter(p => p !== 'none');
  const trunkMeasured = trunkAngleAvg ?? bdc.angles.trunk;
  const trunkVariance = parseFloat(Math.abs(bdc.angles.trunk - tdc.angles.trunk).toFixed(1));

  // Elbow: prefer the assessment that has a reference range
  const elbowAssessment = fitAssessment.bdc.elbow.status !== 'no_range'
    ? fitAssessment.bdc.elbow
    : fitAssessment.tdc.elbow;
  const elbowMeasured = (bdc.angles.elbow + tdc.angles.elbow) / 2;

  return {
    session_id: sessionId,
    timestamp:  new Date().toISOString(),

    rider_profile: {
      bike_type:            bikeType,
      bike_type_label:      bikeLabel,
      position_preference:  ridingStyle,
      experience,
      flexibility,
      // The backend overlays the persisted profile (including structured
      // pain_detail with side and notes) onto this before calling the LLM.
      pain_points:  activePains,
    },

    measurements: {
      bdc_frame: {
        frame_number:      bdc.representativeFrameIndex,
        timestamp_seconds: null,
        angles: {
          knee_bdc: buildAngleEntry(bdc.angles.knee,  fitAssessment.bdc.knee,  'Knee Extension at BDC'),
          trunk_bdc: buildAngleEntry(bdc.angles.trunk, fitAssessment.bdc.trunk, 'Trunk Angle at BDC'),
          elbow_bdc: buildAngleEntry(bdc.angles.elbow, fitAssessment.bdc.elbow, 'Elbow Angle at BDC'),
          hip_bdc:   buildAngleEntry(bdc.angles.hip,   fitAssessment.bdc.hip,   'Hip Angle at BDC (informational)', true),
        },
      },
      tdc_frame: {
        frame_number:      tdc.representativeFrameIndex,
        timestamp_seconds: null,
        angles: {
          hip_tdc:   buildAngleEntry(tdc.angles.hip,   fitAssessment.tdc.hip,   'Hip Closure at TDC'),
          knee_tdc:  buildAngleEntry(tdc.angles.knee,  fitAssessment.tdc.knee,  'Knee Flexion at TDC (informational)', true),
          trunk_tdc: buildAngleEntry(tdc.angles.trunk, fitAssessment.tdc.trunk, 'Trunk Angle at TDC'),
          elbow_tdc: buildAngleEntry(tdc.angles.elbow, fitAssessment.tdc.elbow, 'Elbow Angle at TDC'),
        },
      },
    },

    calculated_adjustments: {
      saddle_height:    buildSaddleHeight(fitAssessment.bdc.knee, bdc.angles.knee),
      handlebar_height: buildHandlebarHeight(fitAssessment.trunkAvg, trunkMeasured),
      stem_length:      buildStemLength(elbowAssessment, elbowMeasured),
      saddle_fore_aft:  buildSaddleForeAft(fitAssessment.tdc.hip, tdc.angles.hip),
    },

    stability: {
      pelvic_rock_mm:     pelvicRock?.mm ?? null,
      pelvic_rock_status: pelvicRock?.status ?? 'no_data',
      pelvic_rock_note:   pelvicRockNote(pelvicRock),
      trunk_angle_variance_degrees: trunkVariance,
      trunk_angle_variance_status:  trunkAngleDivergence ? 'amber' : 'green',
    },

    ranges_applied: {
      base:                  `${bikeLabel} / ${styleLabel}`,
      experience_modifier:   EXPERIENCE_MODIFIER_LABELS[experience],
      flexibility_modifier:  FLEXIBILITY_MODIFIER_LABELS[flexibility],
    },
  };
}
